import {useLayoutEffect, useRef} from 'react'

import type {PrettifiedBlock} from '../hooks/use-prettified-blocks'
import styles from './ChatInput.module.css'
import {PrettifiedCodeBlock} from './PrettifiedCodeBlock'

interface PrettifiedBlocksListProps {
  blocks: PrettifiedBlock[]
  supportsReferenceAttachments: boolean
  onRemove: (id: string) => void
  onToggleCollapse: (id: string) => void
  onCodeChange: (id: string, code: string) => void
  onConvertToFile?: (code: string, id: string) => Promise<void>
}

export function PrettifiedBlocksList({
  blocks,
  supportsReferenceAttachments,
  onRemove,
  onToggleCollapse,
  onCodeChange,
  onConvertToFile,
}: PrettifiedBlocksListProps) {
  const blockElementsRef = useRef<Array<HTMLDivElement | null>>([])
  const prevBlockCountRef = useRef(blocks.length)

  useLayoutEffect(() => {
    if (blocks.length > prevBlockCountRef.current) {
      blockElementsRef.current[blocks.length - 1]?.scrollIntoView({behavior: 'smooth', block: 'nearest'})
    }
    prevBlockCountRef.current = blocks.length
  }, [blocks.length])

  return (
    <div className={styles.prettifiedBlocksContainer} role="group" aria-label="Pasted blocks">
      {blocks.map((block, index) => (
        <div
          key={block.id}
          ref={el => {
            blockElementsRef.current[index] = el
          }}
        >
          <PrettifiedCodeBlock
            code={block.code}
            collapsed={block.collapsed}
            onRemove={() => onRemove(block.id)}
            onToggleCollapse={() => onToggleCollapse(block.id)}
            onCodeChange={newCode => onCodeChange(block.id, newCode)}
            onConvertToFile={
              supportsReferenceAttachments && onConvertToFile ? () => onConvertToFile(block.code, block.id) : undefined
            }
          />
        </div>
      ))}
    </div>
  )
}
